import React from 'react'
import type {
  CustomIconComponentProps,
  IconComponentProps,
} from '@ant-design/icons/lib/components/Icon'
import classNames from 'classnames'

import { insertIconStyles } from './styles'

/** The base svg attributes antd spreads onto every icon glyph. */
export const svgBaseProps = {
  width: '1em',
  height: '1em',
  fill: 'currentColor',
  'aria-hidden': 'true',
  focusable: 'false',
} as const

/**
 * The shadcn-mode rendering of the default export of `@ant-design/icons` - the
 * wrapper that renders a project-owned SVG (`<Icon component={Svg} />`).
 *
 * There is no lucide in here, and that is the point: the ~40 glyphs under
 * `src/icons*` and `src/storybook/wh-icons-antd` are WasteHero's own artwork,
 * not antd's, so the swap must leave them alone. What this file replaces is
 * only antd's frame around them - the same `<span role="img" class="anticon">`
 * with a `1em`, `currentColor` svg inside - so those glyphs render identically
 * in both modes.
 *
 * Ported from `update-design`'s `src/icons/antd-compat/Icon.tsx`; the prop
 * types come from antd's own declarations rather than being re-declared, so
 * `IconComponentProps` keeps meaning exactly what it means today (see the note
 * in `../index.ts` about the package declaring that name twice).
 *
 * ONE deliberate divergence from antd, inherited from that port: `ariaLabel`.
 * antd declares the prop but never destructures it, so it reaches the DOM as
 * the invalid attribute `arialabel` and names nothing; this consumes it and
 * emits a real `aria-label`. Kept because it is strictly better, is reachable
 * only with the toggle on, and no call site in this repo passes the prop.
 * Pinned in `../__tests__/icon-seam-parity.test.tsx`.
 */
const Icon = React.forwardRef<HTMLSpanElement, IconComponentProps>(
  (props, ref) => {
    const {
      className,
      component: Component,
      viewBox,
      spin,
      rotate,
      tabIndex,
      onClick,
      children,
      ariaLabel,
      ...restProps
    } = props

    insertIconStyles()

    const svgStyle = rotate
      ? {
          msTransform: `rotate(${rotate}deg)`,
          transform: `rotate(${rotate}deg)`,
        }
      : undefined

    const innerSvgProps = {
      ...svgBaseProps,
      className: classNames({ 'anticon-spin': !!spin }),
      style: svgStyle,
      ...(viewBox ? { viewBox } : {}),
    } as CustomIconComponentProps & React.SVGProps<SVGSVGElement>

    let iconTabIndex = tabIndex
    if (iconTabIndex === undefined && onClick) iconTabIndex = -1

    const renderInnerNode = () => {
      if (Component) {
        return <Component {...innerSvgProps}>{children}</Component>
      }
      if (children) {
        return (
          <svg {...innerSvgProps} viewBox={viewBox}>
            {children}
          </svg>
        )
      }
      return null
    }

    return (
      // Same clickable `role="img"` span antd renders - see the note in
      // createIcon.tsx; call sites hang onClick straight off the icon.
      /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */
      <span
        role="img"
        aria-label={ariaLabel}
        {...(restProps as React.HTMLAttributes<HTMLSpanElement>)}
        ref={ref}
        tabIndex={iconTabIndex}
        onClick={onClick}
        className={classNames('anticon', className)}
      >
        {renderInnerNode()}
      </span>
    )
  }
)

Icon.displayName = 'AntdIcon'

export default Icon
